import { type ActivityOf, getItem } from '@wyn/content';
import { assertGame } from '../errors.ts';
import { createRng } from '../rng.ts';
import { clamp, type MinigameResult, type TurnEngineImpl, type Ware } from './types.ts';

export interface HagglingState {
  ware: Ware;
  /** The trader's secret limit. Private until the deal is done. */
  limit: number;
  noise: number;
  patience: number;
  counter: number | null;
  say: string;
  /** The trader has lost patience: pawn it or keep it. */
  walkedOff: boolean;
  over: { kind: 'sold' | 'pawned'; price: number } | { kind: 'kept' } | null;
}

export type HagglingMove = { type: 'ask'; price: number } | { type: 'take' } | { type: 'walk' } | { type: 'pawn' } | { type: 'keep' };

export interface HagglingView {
  ware: Ware;
  trader: string;
  patience: number;
  maxPatience: number;
  counter: number | null;
  say: string;
  minPrice: number;
  maxPrice: number;
  pawnPrice: number;
  /** The most the trader will pay for anything. */
  purse: number;
  walkedOff: boolean;
  over: HagglingState['over'];
  /** Revealed once the haggling is over. */
  limit: number | null;
}

/**
 * Traders buy to sell on, so they pay under a good's worth (their limits are in content). Asks run from
 * `HAGGLE_FLOOR` of the worth upwards, counter-offers open a little above it, and the pawnbroker gives
 * less than any trader would.
 */
export const HAGGLE_FLOOR = 0.4;
/** Asks a trader will hear: from the floor (or most of their purse, if that is less) to twice the worth. */
const bounds = (v: number, purse: number) => [Math.max(1, Math.round(Math.min(v * HAGGLE_FLOOR, purse * 0.8))), Math.round(v * 2)] as const;
/** The pawnbroker pays under any trader, and no more than half what the trader carries. */
const pawnPrice = (v: number, purse: number) => Math.max(1, Math.round(Math.min(v * (HAGGLE_FLOOR - 0.05), purse / 2)));
const COUNTER_FROM = HAGGLE_FLOOR + 0.05;
/** For a ware worth more than their purse, a trader's limit is drawn from this share of the purse up to all of it. */
export const HAGGLE_PURSE_FROM = 0.85;

/**
 * The Market Cross: bring one of your goods and talk the price up. Any ask at or under the trader's
 * secret limit is accepted on the spot; above it they lose patience, give a tell (which may lie) and
 * counter. Push too hard and they walk off, leaving the pawnbroker. Ported from the Harrowmere
 * prototype (the Exchange). The ware was taken from the pack at the start; an unsold one comes back.
 */
export const haggling: TurnEngineImpl<ActivityOf<'haggling'>, HagglingState, HagglingMove, HagglingView> = {
  init(a, level, serverSeed, { ware }) {
    assertGame(ware, 'INVALID', 'Bring something to sell.');
    const rng = createRng(serverSeed);
    const [lo, hi] = a.reserve;
    // A trader pays no more than they carry: the Market Cross is a small buyer, and dear goods sell on the market.
    // Nor do they empty their purse for a dear ware, or asking for all of it would always be the answer.
    const want = Math.round(ware.value * (lo + rng.next() * (hi - lo)));
    const limit = want <= a.purse ? want : Math.round(a.purse * (HAGGLE_PURSE_FROM + rng.next() * (1 - HAGGLE_PURSE_FROM)));
    return {
      ware,
      limit,
      // Practice beyond the trader's own level teaches which tells to trust.
      noise: clamp(a.noise - Math.max(0, level - a.level) * 0.015, 0.05, 0.7),
      patience: a.patience,
      counter: null,
      say: `${a.trader} turns it over in their hands. "Well? What do you want for it?"`,
      walkedOff: false,
      over: null,
    };
  },

  move(a, prev, m, { rng }) {
    assertGame(!prev.over, 'INVALID', 'The bargaining is over.');
    const s: HagglingState = { ...prev };
    const V = s.ware.value;
    switch (m.type) {
      case 'ask': {
        assertGame(!s.walkedOff, 'INVALID', 'They have stopped listening.');
        const [min, max] = bounds(V, a.purse);
        assertGame(Number.isInteger(m.price) && m.price >= min && m.price <= max, 'INVALID', `Ask between ${min} and ${max} coin.`);
        if (m.price <= s.limit) {
          s.say = `"${m.price} coin… very well. Done."`;
          s.over = { kind: 'sold', price: m.price };
          return s;
        }
        s.patience--;
        let r = m.price / s.limit;
        if (rng.next() < s.noise) r *= 0.8 + rng.next() * 0.45;
        const tell = r > 1.5 ? 'laughs in your face.' : r > 1.25 ? 'scowls and folds their arms.' : r > 1.08 ? 'strokes their chin, tempted.' : 'hesitates, eyes lingering on your ware.';
        if (s.patience <= 0) {
          s.walkedOff = true;
          s.counter = null;
          s.say = `${a.trader} ${tell} "Enough! I've better things to do."`;
          return s;
        }
        const offer = Math.round(V * COUNTER_FROM + (s.limit - V * COUNTER_FROM) * (0.35 + rng.next() * 0.4) * (1 + (a.patience - s.patience) * 0.15));
        s.counter = Math.min(Math.max(s.counter ?? 0, offer), s.limit);
        s.say = `${a.trader} ${tell} "${m.price} coin? I'll give you ${s.counter}."`;
        return s;
      }
      case 'take':
        assertGame(!s.walkedOff && s.counter !== null, 'INVALID', 'There is no offer on the table.');
        s.say = `"${s.counter} it is."`;
        s.over = { kind: 'sold', price: s.counter };
        return s;
      case 'walk':
        assertGame(!s.walkedOff, 'INVALID', 'Pawn it or keep it.');
        s.over = { kind: 'kept' };
        return s;
      case 'pawn':
        assertGame(s.walkedOff, 'INVALID', 'The trader is still listening.');
        s.over = { kind: 'pawned', price: pawnPrice(V, a.purse) };
        return s;
      case 'keep':
        assertGame(s.walkedOff, 'INVALID', 'The trader is still listening.');
        s.over = { kind: 'kept' };
        return s;
    }
  },

  view(a, s) {
    const [minPrice, maxPrice] = bounds(s.ware.value, a.purse);
    return {
      ware: s.ware,
      trader: a.trader,
      patience: s.patience,
      maxPatience: a.patience,
      counter: s.counter,
      say: s.say,
      minPrice,
      maxPrice,
      pawnPrice: pawnPrice(s.ware.value, a.purse),
      purse: a.purse,
      walkedOff: s.walkedOff,
      over: s.over,
      limit: s.over ? s.limit : null,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const V = s.ware.value;
    const name = getItem(s.ware.itemKey).name.toLowerCase();
    const limitNote = ` They would have gone to ${s.limit}.`;
    if (s.over.kind === 'kept')
      return {
        score: 0.1,
        title: 'No sale',
        text: `You keep your ${s.ware.quality} ${name}.${limitNote}`,
        breakdown: [],
        yield: { itemKey: s.ware.itemKey, quantity: 1, graded: false, quality: s.ware.quality },
        coin: 0,
        xpMult: 1,
      };
    const price = s.over.price;
    if (s.over.kind === 'pawned')
      return {
        score: 0.05,
        title: `Pawned for ${price} coin`,
        text: `The pawnbroker takes your ${name} for ${price} coin.${limitNote}`,
        breakdown: [['Price vs their limit', clamp(price / s.limit, 0, 1)]],
        yield: null,
        coin: price,
        xpMult: 1,
        feats: ['pawned'],
      };
    return {
      score: (() => {
        const [min] = bounds(V, a.purse);
        return s.limit > min ? clamp((price - min) / (s.limit - min), 0, 1) : 1;
      })(),
      title: `Sold for ${price} coin`,
      text: `${a.trader} buys your ${s.ware.quality} ${name} for ${price} coin (worth ${V}).${limitNote}`,
      breakdown: [['Price vs their limit', clamp(price / s.limit, 0, 1)]],
      yield: null,
      coin: price,
      xpMult: 1,
      feats: price >= s.limit ? ['their-limit'] : [],
    };
  },
};
